import { Box, Button, Typography, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { React, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import Chip from "@mui/material/Chip";
import { resolveBillingVerificationStatus } from "../../utils/nextRedemptionBillingRun";
import IconButton from "@mui/material/IconButton";
import WarningIcon from "@mui/icons-material/Warning";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { api } from "../../utils/api";
import { buildRedemptionDateUtcFilter } from "../../utils/redemptionDateRange";
import { formatRedemptionPaymentDate } from "../../utils/redemptionPaymentDate";
import ForcePendingXeroDialog from "./ForcePendingXeroDialog";

const RedemptionTable = ({ redemptions, onRedemptionsUpdate }) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCounts, setTotalCounts] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger for manual refresh
  const [selectionModel, setSelectionModel] = useState([]);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);

  const [filteredRedemptions, setFilteredRedemptions] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Server-side search function
  const performSearch = async (page = 1, searchQuery = {}) => {
    try {
      setLoading(true);

      // For Merchant portal, filter by selected merchant store
      const currentPortal = sessionStorage.getItem("selectedPortal");
      if (currentPortal === "Merchant") {
        const selectedMerchantStore = sessionStorage.getItem("selectedMerchantStore");
        if (selectedMerchantStore) {
          searchQuery.merchantStore = selectedMerchantStore;
        } else {
          const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
          if (currentUser.merchantStore) {
            searchQuery.merchantStore = currentUser.merchantStore;
          } else {
            setFilteredRedemptions([]);
            setLoading(false);
            return;
          }
        }
      }

      console.log("Performing search:", { page, searchQuery, currentPortal });

      const response = await api("v1/redemption/search", "POST", {
        query: searchQuery,
        page: page,
        limit: 100,
        selectedPortal: currentPortal
      });

      console.log("Search response:", response.data);

      if (response.data.success) {
        const newRedemptions = response.data.data.searchResult;
        const newPagination = response.data.data.pagination;
        const newCounts = response.data.data.counts;

        console.log("Setting new state:", {
          redemptions: newRedemptions.length,
          pagination: newPagination,
          counts: newCounts,
          page: page
        });

        setFilteredRedemptions(newRedemptions);
        setPagination(newPagination);
        setTotalCounts(newCounts);
        setCurrentPage(page);

        console.log("State updated successfully");
      } else {
        console.error("Search failed:", response.data.message);
      }
    } catch (error) {
      console.error("Error searching redemptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const buildSearchQuery = () => {
    const query = {};

    const currentPortal = sessionStorage.getItem("selectedPortal");
    if (currentPortal === "Merchant") {
      const selectedMerchantStore = sessionStorage.getItem("selectedMerchantStore");
      if (selectedMerchantStore) {
        query.merchantStore = selectedMerchantStore;
      } else {
        const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
        if (currentUser.merchantStore) {
          query.merchantStore = currentUser.merchantStore;
        }
      }
    }

    const redemptionDateRange = buildRedemptionDateUtcFilter(fromDate, toDate);
    if (redemptionDateRange) {
      query.redemptionDateUTC = redemptionDateRange;
    }

    if (searchTerm) {
      query.searchTerm = searchTerm;
    }

    return query;
  };

  // Initial load - only run once on mount
  useEffect(() => {
    if (hasInitialized) return; // Prevent multiple initial loads

    const currentPortal = sessionStorage.getItem("selectedPortal");

    if (currentPortal === "Merchant") {
      const selectedStore = sessionStorage.getItem("selectedMerchantStore");
      if (redemptions && redemptions.length > 0) {
        const scoped = selectedStore
          ? redemptions.filter((item) => {
              const storeId = item?.merchantStore?._id || item?.merchantStore;
              return String(storeId) === String(selectedStore);
            })
          : [];
        console.log("Initial load: Using redemptions from props (Merchant):", scoped.length);
        setFilteredRedemptions(scoped);
      } else {
        console.log("Initial load: No redemptions prop, performing search (Merchant)");
        performSearch(1, selectedStore ? { merchantStore: selectedStore } : {});
      }
    } else {
      // For Administrator portal, always use server-side search
      console.log("Initial load: Performing server-side search (Administrator)");
      performSearch(1, {});
    }

    setHasInitialized(true);
  }, []); // Empty dependency array - only run on mount

  // Update filteredRedemptions when redemptions prop changes (only for Merchant portal)
  useEffect(() => {
    if (!hasInitialized) return; // Don't run until after initial load

    const currentPortal = sessionStorage.getItem("selectedPortal");

    if (currentPortal === "Merchant") {
      // For Merchant portal, always use the redemptions prop when it's provided
      if (redemptions && Array.isArray(redemptions)) {
        const selectedStore = sessionStorage.getItem("selectedMerchantStore");
        const scoped = selectedStore
          ? redemptions.filter((item) => {
              const storeId = item?.merchantStore?._id || item?.merchantStore;
              return String(storeId) === String(selectedStore);
            })
          : [];
        console.log("Updating filteredRedemptions from props (Merchant):", scoped.length);
        setFilteredRedemptions(scoped);
        // Reset pagination when using props
        setPagination(null);
        setTotalCounts(null);
        setCurrentPage(1);
      } else if (redemptions && redemptions.length === 0) {
        // If explicitly empty array, clear the table
        console.log("Redemptions prop is empty array, clearing table");
        setFilteredRedemptions([]);
        setPagination(null);
        setTotalCounts(null);
        setCurrentPage(1);
      }
    }
    // For Administrator portal, don't update from props - use server-side search only
  }, [redemptions, hasInitialized]);

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log("RedemptionTable state changed:", {
      currentPage,
      filteredRedemptions: filteredRedemptions.length,
      pagination,
      loading,
      redemptionsPropLength: redemptions?.length || 0
    });
  }, [currentPage, filteredRedemptions, pagination, loading, redemptions]);

  const navigate = useNavigate();
  const isAdminPortal = sessionStorage.getItem("selectedPortal") === "Administrator";

  const rows = filteredRedemptions
    .filter((currVal) => currVal && currVal._id) // Filter out invalid entries
    .reduce(
      (acc, currVal, index) => [
        ...acc,
        {
          id: currVal._id || `redemption-${index}`, // Ensure unique ID
          _id: currVal._id, // Keep original _id for reference
          customerName:
            currVal.customerDisplayName ||
            [currVal.vehicle?.customer?.firstName, currVal.vehicle?.customer?.lastName].filter(Boolean).join(" ") ||
            currVal.vehicle?.customer?.email ||
            currVal.vehicle?.customer?.phone ||
            "N/A",
          vehicleInfo: (() => {
            const vin = currVal.vehicle?.vin || "";
            const rego = currVal.vehicle?.rego || "";
            return vin && rego ? `${vin} (${rego})` : vin || rego || "N/A";
          })(),
          productInfo: currVal.product?.name || "N/A",
          merchantInfo: (() => {
            const storeName = currVal.merchantStore?.name || "N/A";
            const userName =
              `${currVal.merchantUser?.firstName || ""} ${currVal.merchantUser?.lastName || ""}`.trim() || "N/A";
            return { storeName, userName };
          })(),
          // Created by: createdVia (admin/merchant) or fallback to createdBy/merchantUser for legacy records
          createdByDisplay: (() => {
            const via = currVal.createdVia;
            if (via === "admin" || currVal.createdBy || currVal.createdByName) {
              return `Admin: ${currVal.createdByName || "Admin"}`;
            }
            if (via === "merchant" || currVal.merchantUser) {
              const userName =
                `${currVal.merchantUser?.firstName || ""} ${currVal.merchantUser?.lastName || ""}`.trim() || "";
              return userName ? `Merchant: ${userName}` : "Merchant";
            }
            return "-";
          })(),
          dateTimeInfo: (() => {
            const redemptionDate = currVal.redemptionDateUTC
              ? new Date(currVal.redemptionDateUTC).toLocaleDateString()
              : "N/A";
            const redemptionTime = currVal.redemptionDateUTC
              ? new Date(currVal.redemptionDateUTC).toLocaleTimeString()
              : "N/A";
            return { redemptionDate, redemptionTime };
          })(),
          paymentDate: formatRedemptionPaymentDate(currVal),
          authorisedBy: currVal.authorisedByName || currVal.authorisedBy || "-",
          authorisedDate: currVal.authorisedDateUTC ? new Date(currVal.authorisedDateUTC).toLocaleDateString() : "-",
          authorisedTime: currVal.authorisedTimeUTC ? new Date(currVal.authorisedTimeUTC).toLocaleTimeString() : "-",
          invoiceStatus: !currVal.active ? "VOIDED" : currVal.invoiceStatus,
          invoiceID: currVal.invoiceID,
          verified: currVal.verified,
          billingVerificationStatus: resolveBillingVerificationStatus(currVal),
          isCoffeePass: currVal.vertical === "cafe",
          active: currVal.active,
          status: currVal.status || "completed", // Add status field
          image: currVal.image, // Add image field
          imagePending: currVal.status === "pending" && (!currVal.image || currVal.image === null || currVal.image === "") // Flag for pending image
        }
      ],
      []
    );

  // Debug: Check for duplicate IDs
  const ids = rows.map((row) => row.id);
  const uniqueIds = [...new Set(ids)];
  if (ids.length !== uniqueIds.length) {
    console.warn(
      "Duplicate IDs found in redemption rows:",
      ids.filter((id, index) => ids.indexOf(id) !== index)
    );
  }

  const columns = [
    {
      field: "invoiceStatus",
      headerName: "Invoice Status",
      width: 140,
      renderCell: (params) => {
        const row = params.row;
        const display = params.value || "N/A";
        const showPaidStatusWarning =
          isAdminPortal &&
          row.verified &&
          row.active &&
          !params.value;
        return (
          <div style={{ padding: "12px 8px", width: "100%" }}>
            <div
              style={{
                lineHeight: "1.4",
                wordBreak: "break-word",
                color: "#000",
                fontSize: "0.9rem",
                whiteSpace: "normal",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                alignItems: "flex-start"
              }}
            >
              <span>{display}</span>
              {showPaidStatusWarning && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#ff9800",
                    fontWeight: "bold",
                    fontSize: "0.75rem"
                  }}
                  title="Verified redemption is missing a paid invoice status in Priority One. This often means Xero sync is delayed or degraded."
                >
                  <WarningIcon sx={{ fontSize: "1rem", color: "#ff9800" }} />
                  <span>Paid status missing</span>
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      field: "billingVerificationStatus",
      headerName: "Billing status",
      minWidth: 140,
      renderCell: (params) => {
        const status = params.value || "unverified";
        if (status === "unverified" && params.row?.isCoffeePass) {
          // No photo to approve; billed from the Coffee Pass bills page.
          return <Chip size="small" label="Coffee Pass bill" color="info" variant="outlined" />;
        }
        const labels = {
          unverified: { label: "Not approved", color: "default" },
          xero_pending: { label: "Pending Xero", color: "warning" },
          verified: { label: "Verified", color: "success" },
          billing_failed: { label: "Billing failed", color: "error" },
        };
        const chip = labels[status] || labels.unverified;
        return <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />;
      },
    },
    {
      field: "customerName",
      headerName: "Customer",
      width: 220,
      renderCell: (params) => (
        <div style={{ padding: "12px 8px", width: "100%" }}>
          <div
            style={{
              lineHeight: "1.4",
              wordBreak: "break-word",
              color: "#000",
              fontSize: "0.9rem",
              whiteSpace: "normal"
            }}
          >
            {params.value || "N/A"}
          </div>
        </div>
      )
    },
    {
      field: "vehicleInfo",
      headerName: "VIN & Rego",
      width: 300,
      renderCell: (params) => (
        <div style={{ padding: "12px 8px", width: "100%" }}>
          <div
            style={{
              lineHeight: "1.4",
              wordBreak: "break-word",
              color: "#000",
              fontSize: "0.9rem",
              whiteSpace: "normal"
            }}
          >
            {params.value || "N/A"}
          </div>
        </div>
      )
    },
    {
      field: "productInfo",
      headerName: "Product",
      width: 250,
      renderCell: (params) => (
        <div style={{ padding: "12px 8px", width: "100%" }}>
          <div
            style={{
              lineHeight: "1.4",
              wordBreak: "break-word",
              color: "#000",
              fontSize: "0.9rem",
              whiteSpace: "normal"
            }}
          >
            {params.value || "N/A"}
          </div>
        </div>
      )
    },
    {
      field: "merchantInfo",
      headerName: "Merchant Store / User",
      width: 260,
      renderCell: (params) => {
        const info = params.value || {};
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "12px 8px", width: "100%" }}>
            <div
              style={{
                lineHeight: "1.4",
                wordBreak: "break-word",
                color: "#000",
                fontSize: "0.9rem",
                whiteSpace: "normal"
              }}
            >
              {info.storeName || "N/A"}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#000",
                lineHeight: "1.4",
                wordBreak: "break-word",
                whiteSpace: "normal"
              }}
            >
              {info.userName || "N/A"}
            </div>
          </div>
        );
      }
    },
    {
      field: "dateTimeInfo",
      headerName: "Redemption Date / Time",
      width: 200,
      renderCell: (params) => {
        const info = params.value || {};
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "12px 8px", width: "100%" }}>
            <div style={{ lineHeight: "1.4", color: "#000", fontSize: "0.9rem" }}>{info.redemptionDate || "N/A"}</div>
            <div style={{ fontSize: "0.75rem", color: "#000", lineHeight: "1.4" }}>{info.redemptionTime || "N/A"}</div>
          </div>
        );
      }
    },
    {
      field: "paymentDate",
      headerName: "Payment date",
      width: 140,
      renderCell: (params) => (
        <div style={{ padding: "12px 8px", width: "100%" }}>
          <div style={{ lineHeight: "1.4", color: "#000", fontSize: "0.9rem" }}>{params.value}</div>
        </div>
      )
    },
    {
      field: "authorisedBy",
      headerName: "Authorised By / Date / Time",
      width: 200,
      renderCell: (params) => {
        const row = params.row;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "12px 8px", width: "100%" }}>
            <div
              style={{
                lineHeight: "1.4",
                wordBreak: "break-word",
                color: "#000",
                fontSize: "0.9rem",
                whiteSpace: "normal"
              }}
            >
              {row.authorisedBy || "-"}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#000", lineHeight: "1.4" }}>{row.authorisedDate || "-"}</div>
            <div style={{ fontSize: "0.75rem", color: "#000", lineHeight: "1.4" }}>{row.authorisedTime || "-"}</div>
          </div>
        );
      }
    },
    // Created by - Admin portal or Merchant redemption flow
    ...(sessionStorage.getItem("selectedPortal") === "Administrator"
      ? [
          {
            field: "createdByDisplay",
            headerName: "Created By",
            width: 200,
            renderCell: (params) => (
              <div style={{ padding: "12px 8px", width: "100%" }}>
                <div
                  style={{
                    lineHeight: "1.4",
                    wordBreak: "break-word",
                    color: "#000",
                    fontSize: "0.9rem",
                    whiteSpace: "normal"
                  }}
                >
                  {params.value || "-"}
                </div>
              </div>
            )
          }
        ]
      : []),
    {
      field: "active",
      headerName: "Status",
      minWidth: 100,
      renderCell: (params) => (
        <span
          style={{
            color: params.value ? "green" : "red",
            fontWeight: "bold",
            fontSize: "0.9rem"
          }}
        >
          {params.value ? "ACTIVE" : "REMOVED"}
        </span>
      )
    },
    // Image Status column - only show for administrators
    ...(sessionStorage.getItem("selectedPortal") === "Administrator"
      ? [
          {
            field: "imagePending",
            headerName: "Image Status",
            minWidth: 150,
            renderCell: (params) => {
              const row = params.row;
              const isPending = row.status === "pending" && (!row.image || row.image === null || row.image === "");
              
              return (
                <div style={{ padding: "12px 8px", width: "100%" }}>
                  {isPending ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#ff9800",
                        fontWeight: "bold",
                        fontSize: "0.9rem"
                      }}
                    >
                      <WarningIcon sx={{ fontSize: "1.2rem", color: "#ff9800" }} />
                      <span>Image Pending</span>
                    </div>
                  ) : row.image ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#4caf50",
                        fontSize: "0.9rem"
                      }}
                    >
                      <CheckBoxIcon sx={{ fontSize: "1.2rem", color: "#4caf50" }} />
                      <span>Uploaded</span>
                    </div>
                  ) : (
                    <span style={{ color: "#999", fontSize: "0.9rem" }}>No Image</span>
                  )}
                </div>
              );
            }
          }
        ]
      : [])
  ];

  // Handle search and filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(1, buildSearchQuery());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [fromDate, toDate, searchTerm]);

  const handleEvent = (params) => navigate(`/redemption/${params.row._id}`);

  const selectedPendingXeroIds = selectionModel.filter((id) => {
    const row = rows.find((r) => r.id === id || r._id === id);
    return row?.billingVerificationStatus === "xero_pending";
  });

  return (
    <div className="w-full flex flex-column items-center text-center" style={{ maxWidth: "100%", overflowX: "auto" }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Typography variant="h6">Filter by a custom redemption date range</Typography>
      <br />

      {/* Search Bar */}
      <div className="w-full mb-4 px-4">
        <TextField
          fullWidth
          label="Search Redemptions"
          placeholder="Search by vehicle rego, VIN, product, merchant, or authorised by..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          disabled={loading}
          sx={{ maxWidth: "800px" }}
          InputProps={{
            endAdornment: loading ? (
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #3498db",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}
              ></div>
            ) : null
          }}
        />
      </div>

      <div className="w-full flex justify-evenly gap-4 mb-4">
        <Box sx={{ marginBottom: "4px" }}>
          From:{" "}
          <DatePicker
            variant="inline"
            dateFormat="dd/MM/yyyy"
            id="date-picker-fromDate"
            margin="normal"
            label="From date"
            placeholderText="dd/mm/YYYY"
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            maxDate={toDate || ""}
            disabled={loading}
          />
        </Box>
        <div>
          To:{" "}
          <DatePicker
            variant="inline"
            dateFormat="dd/MM/yyyy"
            id="date-picker-toDate"
            margin="normal"
            label="To date"
            placeholderText="dd/mm/YYYY"
            selected={toDate}
            onChange={(date) => setToDate(date)}
            minDate={fromDate || ""}
            disabled={loading}
          />
        </div>
        <Button
          variant="contained"
          color="error"
          disabled={(!fromDate && !toDate && !searchTerm) || loading}
          onClick={() => {
            setToDate("");
            setFromDate("");
            setSearchTerm("");
          }}
        >
          Reset
        </Button>
      </div>

      {/* Redemption Counts */}
      {totalCounts && (
        <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "bold", color: "#2e7d32" }}>Valid Redemptions:</span>
                <span
                  style={{
                    backgroundColor: "#e8f5e8",
                    color: "#2e7d32",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                >
                  {totalCounts.totalActive}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "bold", color: "#d32f2f" }}>Removed Redemptions:</span>
                <span
                  style={{
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                >
                  {totalCounts.totalInactive}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontWeight: "bold", color: "#1976d2" }}>Total:</span>
              <span
                style={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontWeight: "bold"
                }}
              >
                {totalCounts.total}
              </span>
            </div>
          </div>
        </div>
      )}

      {isAdminPortal && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 2, px: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchTerm("");
              performSearch(1, { ...buildSearchQuery(), billingVerificationStatus: "xero_pending" });
            }}
          >
            Show Pending Xero
          </Button>
          <Button
            variant="contained"
            color="warning"
            size="small"
            disabled={selectedPendingXeroIds.length === 0}
            onClick={() => setForceDialogOpen(true)}
          >
            Force complete selected ({selectedPendingXeroIds.length})
          </Button>
        </Box>
      )}

      <ForcePendingXeroDialog
        open={forceDialogOpen}
        onClose={() => setForceDialogOpen(false)}
        redemptionIds={selectedPendingXeroIds}
        onComplete={() => {
          setForceDialogOpen(false);
          setSelectionModel([]);
          performSearch(currentPage, buildSearchQuery());
          if (onRedemptionsUpdate) onRedemptionsUpdate();
        }}
      />

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}
          ></div>
          <Typography variant="body1" color="textSecondary">
            Loading redemptions...
          </Typography>
        </div>
      ) : rows.length > 0 ? (
        <>
          <Box
            sx={{
              display: "block",
              overflowX: "auto",
              px: { xs: 1, sm: 2 },
              width: { xs: "100%", sm: "fit-content" },
              maxWidth: { xs: "100%", sm: "calc(100vw - 200px)" }, // Account for sidebar width (150px) + padding/margins on larger screens
              margin: "0 auto",
              "&::-webkit-scrollbar": {
                height: "8px"
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px"
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
                "&:hover": {
                  background: "#555"
                }
              }
            }}
          >
            <DataGrid
              onRowClick={handleEvent}
              rows={rows}
              columns={columns}
              loading={loading}
              checkboxSelection={isAdminPortal}
              disableRowSelectionOnClick
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(model) => setSelectionModel(model)}
              getRowId={(row) => row.id || row._id}
              getRowClassName={(params) => (!params.row.active ? "inactive-redemption-row" : "")}
              rowHeight={100}
              autoHeight={false}
              sx={{
                width: { xs: "100%", sm: "fit-content" },
                minWidth: { xs: "100%", sm: "800px" }, // Full width on mobile, minimum 800px on larger screens
                "& .MuiDataGrid-cell": {
                  overflow: "visible !important",
                  whiteSpace: "normal !important",
                  wordBreak: "break-word",
                  padding: { xs: "8px 4px", sm: "12px 8px" },
                  textOverflow: "clip",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }
                },
                "& .MuiDataGrid-columnHeader": {
                  overflow: "visible !important",
                  whiteSpace: "normal !important",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  padding: { xs: "8px 4px", sm: "8px 8px" }
                },
                "& .MuiDataGrid-columnHeaders": {
                  padding: { xs: "4px 4px", sm: "8px 8px" }
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none"
                },
                "& .inactive-redemption-row": {
                  backgroundColor: "#fef2f2",
                  "&:hover": {
                    backgroundColor: "#fee2e2"
                  }
                }
              }}
            />
          </Box>

          {/* Custom Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    performSearch(currentPage - 1, buildSearchQuery());
                  }
                }}
                disabled={currentPage <= 1}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: currentPage <= 1 ? "#f5f5f5" : "#fff",
                  cursor: currentPage <= 1 ? "not-allowed" : "pointer"
                }}
              >
                Previous
              </button>

              <span style={{ padding: "0 10px" }}>
                Page {currentPage} of {pagination.totalPages}
              </span>

              <button
                onClick={() => {
                  if (currentPage < pagination.totalPages) {
                    performSearch(currentPage + 1, buildSearchQuery());
                  }
                }}
                disabled={currentPage >= pagination.totalPages}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: currentPage >= pagination.totalPages ? "#f5f5f5" : "#fff",
                  cursor: currentPage >= pagination.totalPages ? "not-allowed" : "pointer"
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="error">No records found</p>
      )}
    </div>
  );
};

export default RedemptionTable;
